<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[560px]"
    @close="close"
  >
    <template #header>
      <div class="flex flex-col gap-1 text-left">
        <h2 class="text-xl font-bold text-textPrimary">
          {{ items.length > 1 ? `Reprovar ${items.length} documentos` : 'Reprovar documento' }}
        </h2>
        <p class="text-sm text-[#52526f]">
          <template v-if="items.length === 1">
            {{ items[0].documentName }} · {{ items[0].professionalName }}
          </template>
          <template v-else>Os mesmos motivos valem para todos os selecionados.</template>
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-4 text-left" data-testid="reject-document-modal">
      <div class="flex flex-col gap-2">
        <p class="text-sm font-bold text-textPrimary">
          Motivo(s) da reprovação <span class="text-negative">*</span>
        </p>
        <Checkbox
          v-for="option in REJECTION_REASON_OPTIONS"
          :key="option.value"
          :field-id="`reject-reason-${option.value}`"
          :data-testid="`reject-reason-${option.value}`"
          :label="option.label"
          :value="option.value"
          :checked="form.reasons.includes(option.value)"
          @update:checked="toggleReason(option.value, $event)"
        />
        <p v-if="errors.reasons" class="text-xs text-negative" data-testid="reject-reasons-error">
          {{ errors.reasons }}
        </p>
      </div>

      <!-- Limites do backend (`MaxLength` no DTO de reprovação): Outro ≤ 500, orientação ≤ 1000. -->
      <div
        v-if="form.reasons.includes(ProfessionalDocumentRejectionReason.OTHER)"
        class="flex flex-col gap-1"
      >
        <label class="text-sm font-bold text-textPrimary">
          Descreva o motivo <span class="text-negative">*</span>
        </label>
        <TextareaInput
          v-model="form.otherReason"
          :rows="2"
          :maxlength="500"
          placeholder="Ex.: o nome no documento não confere com o cadastro"
          data-testid="reject-other-input"
        />
        <p v-if="errors.other" class="text-xs text-negative" data-testid="reject-other-error">
          {{ errors.other }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-bold text-textPrimary">Orientação ao médico (opcional)</label>
        <TextareaInput
          v-model="form.guidance"
          :rows="3"
          :maxlength="1000"
          placeholder="Ex.: envie o PDF original, com frente e verso"
          data-testid="reject-guidance-input"
        />
      </div>

      <p class="rounded-lg bg-[#f0f0f4] p-3 text-xs text-[#52526f]">
        O documento volta para <b>Pendente</b>. O médico vê o motivo e a orientação no Drive e
        recebe um push. Você pode desfazer a decisão depois.
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row sm:justify-end gap-3">
        <OutlineButton
          text="Cancelar"
          width="120px"
          height="40px"
          data-testid="reject-document-cancel"
          @click="close"
        />
        <FilledButton
          :text="items.length > 1 ? `Reprovar ${items.length}` : 'Confirmar reprovação'"
          class="bg-negative"
          width="220px"
          height="40px"
          :loading="isSaving"
          :disabled="isSaving"
          data-testid="reject-document-confirm"
          @click="confirm"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import Checkbox from '@/components/designSystem/Checkbox/index.vue'
import TextareaInput from '@/components/designSystem/Textarea/TextareaInput.vue'
import { REJECTION_REASON_OPTIONS } from '@/components/ProfessionalDocuments/labels'
import type { DecisionTarget } from '@/pinia-store/useDocumentReviewStore'
import { ProfessionalDocumentRejectionReason } from '@/types/professional-documents.types'
import type { RejectVersionPayload } from '@/types/professional-documents.types'

import { buildRejectPayload, validateRejectForm } from '../model'
import type { RejectForm } from '../model'

export interface RejectModalItem {
  versionId: number
  userId: number
  documentName: string
  professionalName: string
}

const props = defineProps<{ modelValue: boolean; items: RejectModalItem[]; isSaving: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [targets: DecisionTarget[], payload: RejectVersionPayload]
}>()

const emptyForm = (): RejectForm => ({ reasons: [], otherReason: '', guidance: '' })
const form = ref<RejectForm>(emptyForm())
const errors = ref<{ reasons?: string; other?: string }>({})

// Cada abertura começa sem motivos: o lote seguinte não herda a reprovação anterior.
watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    form.value = emptyForm()
    errors.value = {}
  },
  { immediate: true }
)

/** Payload do `Checkbox` do design system: `{ isChecked, value }`. */
function toggleReason(
  reason: ProfessionalDocumentRejectionReason,
  { isChecked }: { isChecked: boolean }
) {
  const current = form.value.reasons.filter((value) => value !== reason)
  form.value.reasons = isChecked ? [...current, reason] : current
}

function confirm() {
  errors.value = validateRejectForm(form.value)
  if (errors.value.reasons || errors.value.other) return
  emit(
    'confirm',
    props.items.map((item) => ({ versionId: item.versionId, userId: item.userId })),
    buildRejectPayload(form.value)
  )
}

function close() {
  emit('update:modelValue', false)
}
</script>
